import {useEffect, useState} from 'react';
import {api} from '../lib/auth.js';
import {kr} from '../lib/format.js';

/**
 * Everything behind one figure: the transactions on an account in a period, the voucher
 * each belongs to, and the document that caused it.
 *
 * The chain has three links and stopping at any one of them leaves the reader stuck. The
 * AMOUNT is a voucher row; the VOUCHER groups rows into an event and shows what the amount
 * was booked AGAINST — a 1930 row means nothing without the 6570 on the other side; the
 * DOCUMENT is the invoice or payment a human actually recognises. `reference_type` and
 * `reference_number` on the voucher make the third link exact rather than a fuzzy match on
 * a description.
 */
export default function AccountDrill({account, name, period, shadow}) {
	const [data, setData] = useState(null);
	const [error, setError] = useState(null);

	useEffect(() => {
		let live = true;
		setData(null);
		setError(null);
		api(`/api/account/${account}?period=${encodeURIComponent(period)}${shadow ? '&shadow=1' : ''}`)
			.then(r => (r.ok ? r.json() : Promise.reject(new Error(`API svarade ${r.status}`))))
			.then(j => live && setData(j))
			.catch(e => live && setError(e.message));
		return () => { live = false; };
	}, [account, period, shadow]);

	if (error) return <div className="drill"><div className="drill__empty">Kunde inte hämta: {error}</div></div>;
	if (!data) return <div className="drill"><div className="drill__empty">Hämtar transaktioner…</div></div>;

	return (
		<div className="drill">
			<div className="drill__head">
				<b>{account} {name}</b>
				<span className="stamp">{period}</span>
				<span className="stamp">{data.count} transaktioner</span>
				<span className="stamp">summa {kr(data.total)} kr</span>
				<span className="spacer" />
				{!data.files.available && (
					// Never an empty file list. "No attachments" and "we cannot see attachments"
					// look identical on screen and mean opposite things to someone hunting for a
					// receipt, so the reason is stated instead.
					<span className="stamp" title={data.files.reason}>Bilagor: ej tillgängliga</span>
				)}
			</div>

			{data.count === 0 && <div className="drill__empty">Inga transaktioner i perioden.</div>}

			{data.count > 0 && (
				<table className="drill__tbl">
					<thead><tr>
						<th>Datum</th><th>Verifikat</th><th>Beskrivning</th>
						<th>Motkonto</th><th>Belopp</th><th>Underlag</th>
					</tr></thead>
					<tbody>
						{data.rows.map((r, i) => (
							<tr key={`${r.voucher}-${i}`} className={r.shadow ? 'drill__shadow' : undefined}>
								<td>{r.date}</td>
								<td>
									{r.voucher}
									{r.shadow && <span className="pill">skugga</span>}
								</td>
								<td>{r.text || r.description || '—'}</td>
								<td>
									{/* Index-keyed: a voucher legitimately carries two identical rows on the
									    same account (7281 twice at -1 846,15), so account+amount is not unique. */}
									{r.counter.length === 0 ? '—' : r.counter.map((c, ci) => (
										<span key={ci} className="drill__cnt">
											{c.account} <em>{kr(c.amount)}</em>
										</span>
									))}
								</td>
								<td className="num--strong">{kr(r.amount)}</td>
								<td><Doc row={r} /></td>
							</tr>
						))}
					</tbody>
				</table>
			)}
		</div>
	);
}

/** The source document, summarised — and its rows where the mirror carries them. */
function Doc({row}) {
	const [open, setOpen] = useState(false);
	if (row.shadow) return <span className="stamp">{row.note || 'Skuggverifikat'}</span>;
	if (!row.document) {
		return <span className="stamp">{row.reference_type === 'MANUAL' || !row.reference_type
			? 'Manuellt verifikat' : `${row.reference_type} ${row.reference_number ?? ''}`}</span>;
	}
	const d = row.document.doc;
	const who = d.CustomerName ?? d.SupplierName ?? d.InvoiceCustomerName ?? d.InvoiceSupplierName ?? '';
	const rows = d.InvoiceRows ?? d.SupplierInvoiceRows ?? null;
	return (
		<>
			<button className="linkish" onClick={() => setOpen(o => !o)}>
				{row.document.label} {row.document.number}{who ? ` · ${who}` : ''}
			</button>
			{open && (
				<div className="drill__doc">
					{d.InvoiceDate && <div>Fakturadatum {d.InvoiceDate}</div>}
					{d.DueDate && <div>Förfaller {d.DueDate}</div>}
					{d.PaymentDate && <div>Betaldatum {d.PaymentDate}</div>}
					{d.InvoiceNumber != null && <div>Avser faktura {d.InvoiceNumber}</div>}
					{d.Total != null && <div>Totalt {kr(Number(d.Total))} kr</div>}
					{d.Amount != null && <div>Belopp {kr(Number(d.Amount))} kr</div>}
					{d.Balance != null && <div>Kvar {kr(Number(d.Balance))} kr</div>}
					{rows?.length > 0 && (
						<table className="drill__tbl drill__tbl--doc">
							<thead><tr><th>Artikel</th><th>Benämning</th><th>Antal</th><th>Konto</th><th>Belopp</th></tr></thead>
							<tbody>
								{rows.map((r, i) => (
									<tr key={i}>
										<td>{r.ArticleNumber ?? '—'}</td>
										<td>{r.Description ?? r.ItemDescription ?? '—'}</td>
										<td>{r.DeliveredQuantity ?? r.Quantity ?? '—'}</td>
										<td>{r.AccountNumber ?? r.Account ?? '—'}</td>
										<td className="num--strong">{kr(Number(r.Total ?? r.Amount ?? 0))}</td>
									</tr>
								))}
							</tbody>
						</table>
					)}
				</div>
			)}
		</>
	);
}
